// src/store/six-detail/thunks/createFolderThunk.ts
import { createAppAsyncThunk } from "@/store/thunks/createAppAsyncThunk";
import { mapSixFilesDTOToSixFiles, mapSixFoldersDTOToSixFolders } from "@/store/mappers/mapSixDetail";
import type { SixFile, SixFolder } from "@/types/six/Six";
import type { SixFilesApiResponse } from "@/types/six/SixDTOs";
import { withErrorHandling, withLoader } from "@/store/thunks/thunkWrappers";
import { initSixDetail } from "./getInitialDetailThunk";

interface CreateFolderParams {
  sixId: string;
  folderName: string;
  folderPath: string;
}

interface CreateFolderResult {
  files: SixFile[];
  folders: SixFolder[];
}

/**
 * Creates a new folder in a Six.
 * HttpFetcherService auto-fetches the resourceUrl after CQRS polling,
 * returning the full files/folders list directly.
 *
 * @param sixId - The Six to create the folder in
 * @param folderName - The display name for the folder (e.g., 'Legal')
 * @param folderPath - The full path for the folder (e.g., '/Documents/Legal/')
 */
export const createFolder = createAppAsyncThunk<CreateFolderResult, CreateFolderParams>(
  "sixDetail/createFolder",
  withErrorHandling(
    withLoader(async({ sixId, folderName, folderPath }, { extra, dispatch }) => {
      // CQRS polling + auto-fetch returns the full files response
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- auto-fetch returns SixFilesApiResponse
      await extra.sixDetailService.createFolder(sixId, folderName, folderPath) as unknown as SixFilesApiResponse;
      const filesResponse = await extra.sixDetailService.fetchFiles(sixId);
      const files = mapSixFilesDTOToSixFiles(filesResponse.files);
      const folders = mapSixFoldersDTOToSixFolders(filesResponse.folders);
      return { files, folders };
    })
  )
);
